import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMedicalClaimTaxSummary } from '@sentara-health/sentara-api-hooks-core';
import { useTranslation } from 'react-i18next';
import {
  getDecryptedValue,
  UserDetails,
  ApiWrapper,
  NavigateConst,
} from 'common';
import { SelectDropdown, Loader } from '@sentara-health/optimahealth_web';

// Define the main functional component for the yeardropdown
interface YearDropdownProps {
  setMemberValue: (value: any) => void; // replace 'any' with the type of the value
  yearValue: string;
  setYearValue: (value: number) => void;
  fromMedical?: boolean;
}

const YearDropdown = ({
  setMemberValue, //A function to set the selected member's value
  yearValue, // The value of the selected tax year
  setYearValue, //A function to set the selected tax year
  fromMedical, //A boolean flag indicating if the component is used for medical claim tax summary
}: YearDropdownProps) => {
  // Localization
  const { MedicalClaimTaxSummaryType } = useMedicalClaimTaxSummary();
  const { t } = useTranslation();
  const {
    data: taxSummaryTypeData,
    apiError: taxSummaryTypeError,
    callApi: CallTaxSummaryTypeError,
  } = ApiWrapper(MedicalClaimTaxSummaryType);

  const location = useLocation();

  const selectedOption = t('common.selectedOption');
  let callAPi = 0;

  const currentYear: number = new Date().getFullYear();
  const previousToYear: number = currentYear - 1;
  //it initializes various state variables:

  //A boolean state to indicate whether the component is loading data
  const [loader, setLoader] = useState(false);

  //An object containing the current year and the previous year
  const [selectedYear, setSelectedYear] = useState({
    currentYear: currentYear,
    previousToYear: previousToYear,
  });

  //A function from React Router to handle navigation to a specific page
  const navigate = useNavigate();

  //It updates the selectedYear state and performs API calls to fetch the medical claim tax summary data based on the selected year
  const handleYearSelect = (value: string) => {
    setSelectedValue(value);
    setYearValue(value as unknown as number);
    if (yearValue) {
      localStorage.setItem(selectedOption, value);
    }
    callAPi = 1;
    dataFormat(value);
    if (fromMedical) {
      navigate(NavigateConst.dropdownValue);
      setYearValue(value as unknown as number);
    }
  };

  //The 'useMedicalClaimTaxSummary' hook is used to get the required medical claim tax summary type

  const dataFormat = async (yearValue: string) => {
    const memberID = await getDecryptedValue(UserDetails.memberIdData);
    type PostDataType = {
      memberId: string;
      year: string;
    };

    const postData: PostDataType = {
      memberId: memberID,
      year: yearValue ?? currentYear,
    };
    if (memberID) {
      setLoader(true);
      CallTaxSummaryTypeError(postData);
    }
  };

  useEffect(() => {
    if (taxSummaryTypeData) {
      setMemberValue(taxSummaryTypeData);
      setLoader(false);
    } else if (taxSummaryTypeError) {
      setLoader(false);
    }
  }, [taxSummaryTypeData, taxSummaryTypeError]);

  //The 'useEffect' hook is used to fetch the medical claim tax summary data when the component mounts
  useEffect(() => {
    if (callAPi === 0) {
      const item = localStorage.getItem(selectedOption) ?? String(currentYear);
      if (item !== null) {
        dataFormat(item);
      } else {
        dataFormat(currentYear.toString());
      }
    }
  }, []);

  const yearData = localStorage.getItem(selectedOption);
  const [selectedValue, setSelectedValue] = useState<string>(yearData ?? '');

  //The 'dropdownData' function returns a localized string based on the selected tax year, which is used as the default value for the dropdown
  const dropdownData = () => {
    if (yearData === String(previousToYear)) {
      return t('common.previousYear');
    } else if (yearData === String(currentYear)) {
      return t('common.currentYear');
    } else {
      if (location.pathname !== '/home/view-tax-summary') {
        return t('common.taxYear');
      } else {
        return t('common.currentYear');
      }
    }
  };

  return (
    <div className="year_dropdown_container">
      {loader ? <Loader /> : null}

      <SelectDropdown
        dataTestId="viewTaxYearSummaryDropdown"
        className="form-select"
        placeholder={dropdownData()}
        alignment="full"
        value={selectedValue}
        items={[
          {
            label: t('common.currentYear'),
            value: String(selectedYear.currentYear),
          },
          {
            label: t('common.previousYear'),
            value: String(selectedYear.previousToYear),
          },
        ]}
        onItemSelect={(item: { value: string }) => handleYearSelect(item.value)}
      />
    </div>
  );
};
export default YearDropdown;
